import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {ApaleoService} from "crqlar-react-common/dist/services/integrations/apaleo/apaleo.service";
import {Box, Divider, Stack, Tab} from "@mui/material";
import {TabContext} from "@mui/lab";
import Typography from "@mui/material/Typography";
import {ApaleoConfig} from "crqlar-react-common/dist/models/integrations/apaleo/apaleo.config";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import ApaleoConfigForm from "./ApaleoConfigForm";
import ApaleoTasks from "./ApaleoTasks";
import Logs from "./Logs";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {getDefaultAgeCategoryIds} from "src/utils/integrationDefaults.util";


const ApaleoConfigDetails = () => {

    useTrackScreenVisit('system apaleo config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'tasks', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const apaleoConfigData = useEntity<ApaleoConfig>({
        get: async () => {
            return await ApaleoService.find()
        },
        create: ApaleoService.create,
        update: ApaleoService.update,
        delete: (id) => ApaleoService.delete({id: [id]}),
        isValid: (apaleoConfig) => {
            return !!apaleoConfig.name
        },
        cleanupBeforeSave: (apaleoConfig) => {
            return {
                ...apaleoConfig,
                name: apaleoConfig.name?.trim()
            }
        }
    })

    const apaleoConfig = apaleoConfigData.item

    const handleCreate = async () => {
        const [sectors, defaultAgeCategoryIds] = await Promise.all([
            SectorService.getAllSectors({limit: {offset: 0, limit: 1000}}),
            getDefaultAgeCategoryIds()
        ])

        apaleoConfigData.startCreate({
            pollerConfig: {
                fullSync: {
                    profileDaysPast: 1000,
                    reservationDaysPast: 365,
                    staleTimeoutMinutes: 5 * 60,
                },
                quickSync: {
                    staleTimeoutMinutes: 10,
                    syncPeriodMinutes: 5
                }
            },
            dataProcessingRules: {
                accommodationSectorId: sectors.content?.find(s => s.sectorType === SectorType.ACCOMMODATION)?.id,
                ...defaultAgeCategoryIds,
                autoCreateResourceIfNotFound: true,
                autoCreateServiceForRatePlanIfNotFound: true
            }
        } as ApaleoConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={apaleoConfig.name}
            data={apaleoConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={apaleoConfigData.saving}
                onDelete={() => apaleoConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {apaleoConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    apaleoConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!apaleoConfig.id &&
                    <TabContext value={activeTab}>
                        <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                            <Tab label={t('Configuration')} value={'config'}/>
                            <Tab label={t('Tasks')} value={'tasks'}/>
                            <Tab label={t('Stats and Logs')} value={'logs'}/>
                        </ScrollableTabList>
                    </TabContext>
                }

                {
                    !apaleoConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <ApaleoConfigForm apaleoConfigData={apaleoConfigData}/>

                            {
                                !!apaleoConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'tasks' && (
                        <ApaleoTasks configData={apaleoConfigData}/>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs configData={apaleoConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {apaleoConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default ApaleoConfigDetails;
